'use client';
import {useTranslations} from 'next-intl';
import {Rating, Star} from '@smastrom/react-rating';
import {Controller, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from 'zod';
import {ZodValidator} from '@/libs/zod.validator';
import {useBaseMutation} from '@/libs/api/baseRequests';
import {EDIT_PRODUCT_REVIEW_QUERY} from '@/graphql/mutations';

export default function ProductDetailsReviewsEdit({ reviewIdx, review, setReviews, onReviewEditCancel }) {
  const t = useTranslations('product');
  const v = useTranslations('validators');
  
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(
        z.object({
          rating: ZodValidator.rating(v),
          comment: ZodValidator.stringRequired(v, 9),
        })
    ),
    defaultValues: {
      rating: review.value,
      comment: review.comment
    }
  });
  
  const { mutate: editReview } = useBaseMutation(EDIT_PRODUCT_REVIEW_QUERY, {
    toastOnComplete: { toast: true, message: t('reviewEdited') }
  });
  
  const onSubmit = async (formData) => {
    const result = await editReview({
      variables: {
        id: review.id,
        value: formData.rating,
        comment: formData.comment,
      }
    });
    if (result.data) {
      setReviews(prev => prev.map((review, idx) =>
          idx === reviewIdx
              ? { ...review, value: formData.rating, comment: formData.comment }
              : review
      ));
      reset({
        rating: 0,
        comment: ''
      });
      onReviewEditCancel();
    }
  };
  
  return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={`mb-2 form-group ${errors.rating ? 'has-error' : ''}`}>
          <div className="add-a-review">
            <h6>{t('yourRating')}:</h6>
            <Controller
                control={control}
                name="rating"
                render={({field: {onChange, onBlur, value}}) => (
                    <Rating
                        value={value}
                        itemStyles={{
                          itemShapes: Star,
                          activeFillColor: 'var(--ratings)',
                          activeStrokeColor: 'var(--ratings)',
                          inactiveStrokeColor: 'var(--ratings)',
                          itemStrokeWidth: 1
                        }}
                        style={{maxWidth: 110}}
                        onChange={onChange}
                        onBlur={onBlur}
                    />
                )}
            />
          </div>
          {errors.rating && <span className="error-message">{errors.rating.message}</span>}
        </div>
        <div className="reply-comment-field">
          <div className="input-item input-item-textarea ltn__custom-icon">
            <div className={`form-group mb-0 ${errors.comment ? 'has-error' : ''}`}>
            <textarea
                id="comment"
                placeholder={t('typeYourReviews')}
                {...register('comment')}
            />
              {errors.comment && <span className="error-message">{errors.comment.message}</span>}
            </div>
          </div>
          <button
              className="btn theme-btn-3 btn-effect-1 text-uppercase"
              disabled={isSubmitting}
              type="reset"
              onClick={onReviewEditCancel}
          >
            {t('cancel')}
          </button>
          <button
              className="btn theme-btn-1 btn-effect-1 text-uppercase"
              type="submit"
              disabled={isSubmitting}
          >
            {isSubmitting ? t('submitting') : t('submit')}
          </button>
        </div>
      </form>
  );
};
